import React, { useEffect } from 'react';

import type { RoleId } from '@vlabs/api/artemy/model';
import validate from '@vlabs/shared/validators';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Input } from '@vlabs/uikit/components/controls/input/Input';
import { useForm } from 'react-hook-form';

import { RoleSelect } from '@vlabs/monokit/shared/ui/role-select';

import { useUserFormTranslation } from './i18n';
import st from './UserForm.module.sass';

export type UserFormContext = 'create' | 'update' | 'duplicate';
export interface UserFormValues {
  email?: string;
  full_name?: string;
  role_id?: RoleId;
  password?: string;
}

export interface UserFormProps {
  defaultValues?: UserFormValues;
  duplicateSourceName?: string;
  onSubmit: (values: UserFormValues) => void | Promise<void>;
  onCancel?: VoidFunction;
  context?: UserFormContext;
}

const formDefaultValues: UserFormValues = {
  email: '',
  full_name: '',
  role_id: undefined,
};

function UserForm({
  onSubmit,
  defaultValues = undefined,
  duplicateSourceName = undefined,
  onCancel = undefined,
  context = 'create',
}: UserFormProps): React.JSX.Element {
  const { t } = useUserFormTranslation();
  const duplicateUserName = (
    duplicateSourceName
    ?? defaultValues?.full_name
    ?? defaultValues?.email
  );
  const form = useForm({
    defaultValues: {
      ...formDefaultValues,
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (!defaultValues) return;
    form.reset({
      ...formDefaultValues,
      ...defaultValues,
    });
  }, [defaultValues, form]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div
        className={st.Root}
        data-testid="userForm"
      >
        <h5>{t('title', { context })}</h5>

        {context === 'duplicate' && (
          <span className={st.Subtitle}>
            {t('subtitle', { name: duplicateUserName })}
          </span>
        )}

        <div className={st.InputWrapper}>
          <div className={st.Input}>
            <Input
              id="email"
              label={t('email_label')}
              {...form.register('email', {
                required: validate.required(),
                maxLength: validate.maxLength(128),
                validate: validate.email(),
              })}
              errors={form.formState.errors}
            />
          </div>

          <div className={st.Input}>
            <Input
              id="name"
              label={t('name_label')}
              {...form.register('full_name', {
                required: validate.required(),
                minLength: validate.minLength(3),
                maxLength: validate.maxLength(128),
                validate: validate.latinName(),
              })}
              errors={form.formState.errors}
            />
          </div>

          {context !== 'update' && (
            <div className={st.Input}>
              <Input
                id="password"
                label={t('password_label')}
                type="password"
                {...form.register('password', {
                  required: validate.required(),
                  minLength: validate.minLength(3),
                  maxLength: validate.maxLength(128),
                })}
                errors={form.formState.errors}
              />
            </div>
          )}

          <div className={st.Input}>
            <RoleSelect
              errors={form.formState.errors}
              id="role_id"
              label={t('role_label')}
              name="role_id"
              onChange={(newValue) => {
                form.setValue('role_id', newValue);
              }}
              placeholder={t('role_placeholder')}
              value={form.watch('role_id')}
            />
          </div>
        </div>
        <div className={st.Actions}>
          <Button
            className={st.Button}
            data-testid="userForm.submitButton"
            type="submit"
          >
            {t('submit', { context })}
          </Button>

          <Button
            className={st.Button}
            data-testid="userForm.cancelButton"
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}

export { UserForm };
